import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/api";

const PatientAuthContext = createContext(null);

// Create axios instance with credentials (auto-sends httpOnly cookies)
const patientApi = axios.create({
  baseURL: API,
  withCredentials: true,  // Enable cookie sending
});

export function PatientAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Call /auth/patient/me to verify token and get user data
      // Browser auto-sends httpOnly cookie with this request
      const { data } = await patientApi.get("/auth/patient/me");
      
      if (data?.role === "patient") {
        setUser(data);
      // Auto-fetch prescriptions for dashboard
      try {
        const rxResponse = await patientApi.get("/patient/prescriptions/");
        if (rxResponse.data?.prescriptions) {
          setUser({
            ...data,
            prescriptionCount: rxResponse.data.prescriptions.length
          });
        }
      } catch (err) {
        console.log("Could not fetch prescriptions");
      }
      } else {
        setUser(false);
      }
    } catch (err) {
      // Token expired or invalid
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check authentication on app load
  useEffect(() => {
    refresh();
  }, [refresh]);

  const setSession = (data) => {
    // New login - backend already set httpOnly cookie
    // We just update the local user state
    if (data?.user) {
      setUser(data.user);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear cookie on server side
      await patientApi.post("/auth/patient/logout");
    } catch (_) {
      // Ignore errors - we'll clear local state anyway
    }
    
    // Clear local user state
    setUser(false);
  };

  return (
    <PatientAuthContext.Provider value={{ user, loading, setSession, logout, refresh, api: patientApi }}>
      {children}
    </PatientAuthContext.Provider>
  );
}

export const usePatientAuth = () => useContext(PatientAuthContext);
export { patientApi };
